import moment from "moment";
import { SuscriptionTypeApi, SuscriptionTypeDataType } from "./SuscriptionTypeApi";
import { ApiClient } from "./ApiClient";
import { TenantApi, TenantDataType } from "./TenantApi";
import { getTenantId } from "@utils/Auth";

export type PlanUpgradeSession = {
    onvoCustomerId: string;
    onvoSubscriptionId: string;
}

export const activeOnvoSuscription = async (tenantData: TenantDataType): Promise<boolean> => {
    const suscriptionType = await SuscriptionTypeApi.get(`${tenantData.suscriptionTypeId}`);
    const nextMonth = moment().add(1, "month").startOf("month")
    const daysDifference = nextMonth.diff(moment(), 'days') + 1;
    const cycleAnchor = moment().add(1, "month").startOf("month").toISOString();

    const { data: response } = await ApiClient.mutations.onvoManageSubscription({
        input: {
            activate: true,
            // billingCycleAnchor: cycleAnchor,
            customerId: tenantData.onvoCustomerId,
            // trialDays: daysDifference,
            productPriceId: suscriptionType.onvoPriceId,
        }
    })
    if (response?.ok) {
        const newTenantId = {
            ...tenantData,
            onvoSubscriptionId: response?.subscription?.id
        }
        await TenantApi.update(`${tenantData.id}`, newTenantId)
        return true
    } else {
        throw new Error("Suscripcion Onvo no creada")
    }
}

export const deactiveOnvoSuscription = async (tenantData: TenantDataType): Promise<boolean> => {
    const tenantId = await getTenantId();
    const { data: response } = await ApiClient.mutations.onvoManageSubscription({
        input: {
            cancel: true,
            subscriptionId: tenantData.onvoSubscriptionId,
            cancelAtPeriodEnd: true,
            tenantId
        }
    })
    if (response?.ok) {
        const newTenantId: TenantDataType = {
            ...tenantData,
            onvoSubscriptionId: null,
            status: "toCancel",
        }
        await TenantApi.update(`${tenantData.id}`, newTenantId, true)
        return true
    } else {
        throw new Error("Suscripcion Onvo no cancelada")
    }
}

/**
 * Inicia el upgrade a un plan de pago para un tenant (típicamente en plan gratis):
 * 1. Asegura un onvoCustomerId (lo crea vía onvoProxy POST /customers si falta).
 * 2. Crea la suscripción ONVO del plan destino (onvoManageSubscription activate).
 *
 * No cambia todavía suscriptionTypeId/tipoPago/status del tenant: eso ocurre solo
 * tras el pago exitoso en el widget ONVO. Devuelve los ids necesarios para renderizarlo.
 */
export const startPlanUpgrade = async (
    tenantData: TenantDataType,
    targetPlan: SuscriptionTypeDataType
): Promise<PlanUpgradeSession> => {
    if (!targetPlan.onvoPriceId) {
        throw new Error("El plan seleccionado no tiene un precio de ONVO configurado");
    }

    let onvoCustomerId = tenantData.onvoCustomerId;

    if (!onvoCustomerId) {
        const { data: customerResponse } = await ApiClient.mutations.onvoProxyMutation({
            input: {
                path: "/customers",
                method: "POST",
                body: JSON.stringify({
                    email: tenantData.email,
                    name: tenantData.nombre,
                    phone: `+${tenantData.telefono1_cd || ""}${tenantData.telefono1 || ""}`,
                }),
            }
        });

        // El campo `data` del proxy puede llegar como objeto o como string JSON.
        const rawData = customerResponse?.data;
        const parsedCustomer = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
        const createdCustomerId = (parsedCustomer as { id?: string } | null)?.id;
        if (!customerResponse?.ok || !createdCustomerId) {
            throw new Error("No fue posible crear el cliente en ONVO");
        }
        onvoCustomerId = createdCustomerId;
        await TenantApi.update(`${tenantData.id}`, { onvoCustomerId });
    }

    const { data: subscriptionResponse } = await ApiClient.mutations.onvoManageSubscription({
        input: {
            activate: true,
            customerId: onvoCustomerId,
            productPriceId: targetPlan.onvoPriceId,
        }
    });

    const onvoSubscriptionId = subscriptionResponse?.subscription?.id;
    if (!subscriptionResponse?.ok || !onvoSubscriptionId) {
        throw new Error("No fue posible crear la suscripcion en ONVO");
    }

    return { onvoCustomerId, onvoSubscriptionId };
}